import classNames from 'classnames'
import AddToCartButton from '../add-to-cart-button'
import { ProductTypeProps } from '.'
import { useResponsive, useTranslations } from '@dustin-web/microsite-components/src/hooks'
import { DigitalDataOrigin } from '../utils/digital-data-types'
import { Card } from './product-card-gallery-composable'
import type { Props as HeadingProps } from '@dustin-web/microsite-components/src/heading'

type Props = {
  product: ProductTypeProps
  ticket: string
  slim?: boolean
  withBorder?: boolean
  imagePriority?: boolean
  isExtraItem?: boolean
  inFilterList?: boolean
  sizeClasses?: string
  mainProductErpIdentifier?: string
  digitalDataOrigin: DigitalDataOrigin
  digitalIndex?: number
  isProductAccessories?: boolean
  preventSetLastUpdatedProductErpIdentifier?: boolean
  hTag?: HeadingProps['hTag']
}

const ProductCardStrippedMini = ({
  product,
  ticket,
  withBorder = false,
  imagePriority = false,
  isExtraItem = false,
  inFilterList = false,
  sizeClasses = '',
  mainProductErpIdentifier,
  digitalDataOrigin,
  digitalIndex = 0,
  preventSetLastUpdatedProductErpIdentifier,
  hTag,
}: Props) => {
  const { availability } = product
  const { breakpoints } = useResponsive()
  const { translate } = useTranslations()

  const containerClasses = classNames(sizeClasses, 'u-color-black', {
    'c-filter__extra-gallery-box': isExtraItem,
    'col-12 col-sm-6 col-md-4': inFilterList && !sizeClasses.length,
    'col-12 col-sm-6 col-md-4 col-lg-3': !inFilterList && !sizeClasses.length,
  })

  return (
    <Card.Container
      customClassName={containerClasses}
      digitalDataOrigin={digitalDataOrigin}
      digitalIndex={digitalIndex}
      product={product}>
      <div
        className={classNames('c-product-card c-product-card--stripped d-flex flex-column', {
          'u-border u-border-light': withBorder,
        })}>
        <Card.Picture
          product={product}
          ticket={ticket}
          imageWidth={148}
          imageHeight={88}
          imagePriority={imagePriority}
          digitalDataOrigin={digitalDataOrigin}
          digitalDataIndex={digitalIndex}
        />
        <Card.Content>
          <Card.Top>
            <Card.Title
              product={product}
              ticket={ticket}
              digitalDataOrigin={digitalDataOrigin}
              digitalDataIndex={digitalIndex}
              hTag={hTag}
              ellipsis
            />
          </Card.Top>
          <div>
            <Card.Stars reviewCount={product.reviewCount} reviewScore={product.reviewScore} />
            <Card.Badge
              isAvailableForSale={product.availability?.isAvailableForSale}
              price={product.price}
              isNewProduct={product.isNewProduct}
            />

            <Card.Energy
              energyClass={product.energyClass}
              energyDocumentId={product.energyDocumentId}
              energyLabelImageId={product.energyLabelImageId}
              id={product.id}
              nameSlug={product.nameSlug}
              manufacturerName={product.manufacturerName}
              displaySpecifications={product.displaySpecifications}
              displayName={product.displayName}
              textAlignment={breakpoints.min.LG && !breakpoints.min.XL ? 'bottom' : 'right'}
              isMinSmallMobile={breakpoints.min.SM}
            />
            <div className="d-flex">
              <Card.Price
                price={product.price}
                isAvailableForSale={product.availability?.isAvailableForSale}
                stripped={true}
              />
              <Card.ButtonRow>
                <AddToCartButton
                  product={product}
                  ticket={ticket}
                  isAd={false}
                  addToCartText={translate('Add')}
                  mainProductErpIdentifier={mainProductErpIdentifier ?? null}
                  quantity={1}
                  digitalDataOrigin={digitalDataOrigin}
                  digitalDataIndex={digitalIndex}
                  showMiniBtn={false}
                  minWidth={false}
                  shoppingCartIcon
                  primary={false}
                  preventSetLastUpdatedProductErpIdentifier={
                    preventSetLastUpdatedProductErpIdentifier
                  }
                  buttonClassName="u-border minicart-recommendation--add-to-cart-button u-bg-white u-color-black"
                />
              </Card.ButtonRow>
            </div>
            <Card.ActionRow>
              <Card.Availability
                availability={availability}
                isBid={product.price?.isBid}
                compactStatus
              />
              <Card.Compare
                id={product.id}
                displayName={product.displayName}
                categoryName={product.categoryName}
              />
            </Card.ActionRow>
          </div>
        </Card.Content>
      </div>
    </Card.Container>
  )
}

export default ProductCardStrippedMini
